import axios from "@/utils/axios";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  useInfiniteQuery,
} from "@tanstack/react-query";
import type { Meta } from "./data-center.types";

type addDataToTable = Record<string, string | boolean | number>;

export const useAddDataToTable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      table_name,
      data,
    }: {
      table_name: string;
      data: addDataToTable;
    }) => {
      const response = await axios.post(
        `/admin/v1/data-center/tables/add-data`,
        { table_name, data },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

export const useUpdateRowData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      table_name,
      data,
    }: {
      id: string;
      table_name: string;
      data: addDataToTable;
    }) => {
      const response = await axios.patch(
        `/admin/v1/data-center/tables/data/${id}`,
        { table_name, data },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

export const useDeleteRowData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      table_name,
    }: {
      id: string;
      table_name: string;
    }) => {
      const response = await axios.delete(
        `/admin/v1/data-center/tables/data/${id}`,
        { data: { table_name } },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

export const useDeleteMultipleRowsData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ids,
      table_name,
    }: {
      ids: string[];
      table_name: string;
    }) => {
      const response = await axios.patch(
        `/admin/v1/data-center/tables/data/massive-delete`,
        { records_ids: ids, table_name },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["data-center", "tables"] });
    },
  });
};

// Obtener información de una tabla (por nombre)
export const useGetTableData = (
  name: string,
  source: string = "SDT",
  paginate: number = 0,
  filters:
    | Array<{
        name: string;
        operator?: string;
        type: string;
        value: string | number | boolean | null;
      }>
    | undefined
    | null = [],
) => {
  const normalizedFilters = filters || [];

  return useQuery({
    queryKey: [
      "data-center",
      "table-data",
      name,
      source,
      paginate,
      normalizedFilters,
    ],
    queryFn: async () => {
      const response = await axios.get("/admin/v1/data-center/tables/data", {
        params: {
          table_name: name,
          source,
          filters: normalizedFilters,
          paginate,
        },
      });
      return response.data;
    },
    enabled: !!name,
  });
};

export const useGetTableDataInfinite = ({
  name,
  source = "SDT",
  search = "",
  order_by,
  per_page = 60,
}: {
  name: string;
  source?: string;
  search?: string;
  order_by?: string;
  per_page?: number;
}) => {
  return useInfiniteQuery({
    queryKey: [
      "data-center",
      "table-data-infinite",
      name,
      source,
      search,
      order_by,
      per_page,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await axios.get("/admin/v1/data-center/tables/data", {
        params: {
          table_name: name,
          source,
          paginate: 1,
          page: pageParam,
          per_page,
          ...(order_by && { order_by }),
          filter: {
            ...(search && { search }),
          },
        },
      });
      return response.data;
    },
    enabled: !!name,
    initialPageParam: 1,
    getNextPageParam: ({ data }) => {
      const { current_page, last_page } = data as Meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
};

export const getTableData = async (
  table_name: string,
  column_name: string,
  page: number = 1,
) => {
  try {
    if (!table_name || !column_name) return;
    const response = await axios.get("/admin/v1/data-center/tables/data", {
      params: {
        table_name,
        paginate: 1,
        per_page: 15,
        page: page,
      },
    });
    return response?.data?.data;
  } catch (error) {
    console.error(`Error fetching data from table:`, error);
    return [];
  }
};

export const getDataCenterDataById = async (table_name: string, id: string) => {
  const response = await axios.get(`/admin/v1/data-center/tables/data`, {
    params: {
      table_name,
      "filters[0][name]": "id",
      "filters[0][operator]": "EQUALS",
      "filters[0][value]": id,
      "filters[0][type]": "STRING",
    },
  });
  return response?.data;
};

export const useGetDataCenterDataById = (
  tableName: string,
  id: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["data-center-data-by-id", tableName, id],
    queryFn: async () => {
      return getDataCenterDataById(tableName, id);
    },
    enabled: enabled && !!tableName && !!id,
  });
};
